import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Facebook, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LOGO_URL, NAV_ITEMS, CONTACT_INFO } from "@/data/siteData";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Always show scrolled style (white background, colored logo)
  const showScrolledStyle = true;

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showScrolledStyle
          ? "bg-white/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={LOGO_URL}
              alt="Outsource Point"
              className="h-10 md:h-12 transition-all duration-300"
              data-testid="logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_ITEMS.map((item) => {
              const isActive =
                item.href === "/"
                  ? location.pathname === "/"
                  : location.pathname === item.href ||
                    location.pathname.startsWith(`${item.href}/`);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`nav-link relative ${
                    !showScrolledStyle 
                      ? isActive 
                        ? "text-[#2ED573] font-semibold" 
                        : "text-white hover:text-[#2ED573]"
                      : isActive 
                        ? "text-[#2E4A91] font-semibold" 
                        : "text-slate-500 hover:text-[#2E4A91]"
                  }`}
                  data-testid={`nav-${item.href.replace("/", "") || "home"}`}
                >
                  {item.label}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#2ED573] rounded-full" />
                  )}
                </Link>
              );
            })}
            <Link to="/zapazi-chas">
              <Button
                className={`rounded-lg px-6 ${
                  location.pathname === "/zapazi-chas" 
                    ? "bg-[#2E4A91] text-white hover:bg-[#243b75]" 
                    : "btn-secondary"
                }`}
                data-testid="nav-appointment-btn"
              >
                Запазете час
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button
                data-testid="mobile-menu-btn"
                className={`p-2 rounded-lg ${
                  showScrolledStyle ? "text-slate-700" : "text-white"
                }`}
              >
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white overflow-y-auto">
              <div className="flex flex-col gap-5 mt-6 pb-8">
                <img
                  src={LOGO_URL}
                  alt="Outsource Point"
                  className="h-10 w-auto object-contain mb-2"
                />
                {NAV_ITEMS.map((item) => {
                  const isActive =
                    item.href === "/"
                      ? location.pathname === "/"
                      : location.pathname === item.href ||
                        location.pathname.startsWith(`${item.href}/`);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`font-medium text-base transition-colors ${
                        isActive 
                          ? "text-[#2E4A91] font-bold border-l-4 border-[#2ED573] pl-3 -ml-3" 
                          : "text-slate-700 hover:text-[#2E4A91]"
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
                <Link to="/zapazi-chas" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    className={`rounded-lg mt-2 w-full ${
                      location.pathname === "/zapazi-chas"
                        ? "bg-[#2E4A91] text-white hover:bg-[#243b75]"
                        : "btn-secondary"
                    }`}
                  >
                    Запазете час
                  </Button>
                </Link>

                {/* Divider and Social Links */}
                <div className="border-t border-slate-200 pt-4 mt-2">
                  <p className="text-sm text-slate-500 mb-4">Социални мрежи</p>
                  <div className="flex gap-4">
                    <a
                      href={CONTACT_INFO.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Outsource Point във Facebook"
                      className="w-10 h-10 rounded-lg bg-[#1877F2] flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a
                      href={CONTACT_INFO.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Outsource Point в LinkedIn"
                      className="w-10 h-10 rounded-lg bg-[#0A66C2] flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                      data-testid="mobile-menu-linkedin-link"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
